import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  MsalService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject } from 'rxjs';

export const AppRole = {
  BUSINESS: 'HRM.Business.Users',
  SUPPORT: 'HRM.Prod.Support',
  READ: 'HRM.Read.Only.Users',
};

export interface UserAuthorization {
  read: boolean;
  write: boolean;
  maintenance: boolean;
}

export interface AuthenticatedUser {
  name?: string;
  username?: string;
  idTokenClaims?: any;
  authorization: UserAuthorization;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly msalGuardConfig = inject(MSAL_GUARD_CONFIG);
  private readonly msalService = inject(MsalService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  private readonly _user = new BehaviorSubject<AuthenticatedUser | null>(null);
  private readonly _startupError = new BehaviorSubject<string | null>(null);

  private readonly requiredRoles = {
    read: [AppRole.BUSINESS, AppRole.SUPPORT, AppRole.READ],
    write: [AppRole.BUSINESS, AppRole.SUPPORT],
    maintenance: [AppRole.BUSINESS, AppRole.SUPPORT],
  };

  init(): void {
    this.checkAndSetActiveAccount();
  }

  checkAndSetActiveAccount(): void {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }

    if (activeAccount) {
      // Clear redirect flag since we have an account
      sessionStorage.removeItem('hrm.redirect.attempted');
      
      const authorization = this.applyAuthorization(activeAccount);
      this._user.next({ ...activeAccount, authorization });

      if (!authorization.read) {
        this.router.navigate(['/unauthorized']);
      } else {
        if (this.location.path().includes('/unauthorized')) {
          this.router.navigate(['/portal']);
        }
      }
    } else {
      // This method runs only after MSAL reports that no interaction is active.
      // Any remaining marker is stale state from an interrupted/expired session,
      // not a redirect that the application should wait for indefinitely.
      sessionStorage.removeItem('msal.interaction.status');
      sessionStorage.removeItem('hrm.redirect.attempted');
      sessionStorage.setItem('hrm.redirect.attempted', 'true');
      this.loginRedirect();
    }
  }

  loginRedirect(): void {
    const request = this.msalGuardConfig.authRequest
      ? this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest)
      : this.msalService.loginRedirect();

    request.subscribe({
      error: () => {
        sessionStorage.removeItem('hrm.redirect.attempted');
        this._startupError.next('Your sign-in session could not be renewed. Please try again.');
      },
    });
  }

  signOut(): void {
    this.msalService.logoutRedirect();
  }

  getUser() {
    return this._user.asObservable();
  }

  getUserValue(): AuthenticatedUser | null {
    return this._user.getValue();
  }

  getStartupError() {
    return this._startupError.asObservable();
  }

  reportStartupFailure(): void {
    sessionStorage.removeItem('msal.interaction.status');
    sessionStorage.removeItem('hrm.redirect.attempted');
    this._startupError.next('Your sign-in session could not be restored. Please try again.');
  }

  retryLogin(): void {
    this._startupError.next(null);
    this.checkAndSetActiveAccount();
  }

  private applyAuthorization(activeAccount: any): UserAuthorization {
    const authorized: UserAuthorization = {
      read: false,
      write: false,
      maintenance: false,
    };

    if (activeAccount?.idTokenClaims?.roles) {
      const userRoles = activeAccount.idTokenClaims.roles;

      if (userRoles.filter((role: string) => this.requiredRoles.read.includes(role)).length > 0) {
        authorized.read = true;
        localStorage.setItem('user_access', 'read');
      }

      if (userRoles.filter((role: string) => this.requiredRoles.write.includes(role)).length > 0) {
        authorized.write = true;
        localStorage.setItem('user_access', 'write');
      }

      if (
        userRoles.filter((role: string) => this.requiredRoles.maintenance.includes(role)).length > 0
      ) {
        authorized.maintenance = true;
      }
    }

    return authorized;
  }
}
